import type {
  EnhancedProductFamily,
  EnhancedProductFamilyId,
  EnhancedProductVariant,
  EnhancedProductVariantId,
} from '#api/enhancements/enhancedTmsApiTypes.ts'
import type { Tg3ToMaicConversionResponse } from '#api/tmsApiService'

export interface Tg3CodeEntry {
  code: string
  variantTitle: string
  familyTitle: string
  // define usable values for transformationTier (are "2" and "1" right now)
  transformationTier: string
}

export interface Tg3CConversionInsight {
  confidence: number
  tg3Codes: Tg3CodeEntry[]
}

type Tg3ConversionInsightsLookupKey = `${EnhancedProductFamilyId}${EnhancedProductVariantId}`

export type Tg3ConversionInsightsLookup = Record<Tg3ConversionInsightsLookupKey, Tg3CConversionInsight[]>

export function mapTg3ConversionInsights(
  tmsMapping: Tg3ToMaicConversionResponse,
  transformedTg3VariantsById: Map<EnhancedProductVariant['id'], EnhancedProductVariant> | undefined,
  transformedTg3FamilyByVariantId: Map<EnhancedProductVariant['id'], EnhancedProductFamily> | undefined,
): Tg3ConversionInsightsLookup | undefined {
  if (!tmsMapping.vCodesToFPCCodes?.codeTransformationResults || tmsMapping.vCodesToFPCCodes.codeTransformationResults.length <= 0) {
    return undefined
  }

  const lookup: Tg3ConversionInsightsLookup = {}

  for (const result of tmsMapping.vCodesToFPCCodes.codeTransformationResults) {
    if (!result.fpcCodes || result.fpcCodes.length === 0 || !result.tg3VCodes || result.tg3VCodes.length === 0) {
      continue
    }

    const tg3Codes: Tg3CodeEntry[] = result.tg3VCodes.map((tg3VCode) => {
      const transformationTier = result.transformationTiers?.[tg3VCode] ?? 'unknown'
      const asVariantId = tg3VCode as EnhancedProductVariantId
      const variant = transformedTg3VariantsById?.get(asVariantId)
      const family = transformedTg3FamilyByVariantId?.get(asVariantId)
      return {
        code: tg3VCode,
        variantTitle: variant?.title ?? 'Unknown Variant',
        familyTitle: family?.title ?? 'Unknown Family',
        transformationTier,
      }
    })

    const insight: Tg3CConversionInsight = {
      confidence: result.confidence ?? 0,
      tg3Codes: tg3Codes,
    }

    for (const fpcCode of result.fpcCodes) {
      const asVariantId = fpcCode as Tg3ConversionInsightsLookupKey
      lookup[asVariantId] = lookup[asVariantId] ?? []
      lookup[asVariantId].push(insight)
    }
  }

  return lookup
}
